import { useState, useRef } from 'react'
import { authFetch, apiUrl } from '../../lib/api'

type Phase = 'idle' | 'planning' | 'searching' | 'reading' | 'synthesizing' | 'done' | 'error'

interface Source { title: string; url: string }
interface ProgressEvent { message: string }

export default function ResearchPanel() {
  const [query, setQuery] = useState('')
  const [phase, setPhase] = useState<Phase>('idle')
  const [progress, setProgress] = useState<ProgressEvent[]>([])
  const [report, setReport] = useState('')
  const [sources, setSources] = useState<Source[]>([])
  const [error, setError] = useState('')
  const abortRef = useRef<AbortController | null>(null)

  const startResearch = async () => {
    if (!query.trim()) return
    setPhase('planning')
    setProgress([])
    setReport('')
    setSources([])
    setError('')

    const controller = new AbortController()
    abortRef.current = controller

    try {
      const res = await authFetch(apiUrl('/api/research'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: query.trim() }),
        signal: controller.signal,
      })

      if (!res.ok || !res.body) { setError('Research failed'); setPhase('error'); return }

      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buf = ''
      let reportText = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buf += decoder.decode(value, { stream: true })
        const lines = buf.split('\n')
        buf = lines.pop() ?? ''

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          try {
            const data = JSON.parse(line.slice(6))
            if (data.type === 'phase') {
              setPhase(data.phase)
              setProgress(p => [...p, { message: data.message }])
              if (data.sources) setSources(data.sources)
            } else if (data.type === 'progress') {
              setProgress(p => [...p, { message: data.message }])
            } else if (data.type === 'text') {
              reportText += data.text
              setReport(reportText)
            } else if (data.type === 'done') {
              setPhase('done')
              if (data.sourcesUsed) setSources(data.sourcesUsed)
            } else if (data.type === 'error') {
              setError(data.message)
              setPhase('error')
            }
          } catch {}
        }
      }
      if (phase !== 'error') setPhase('done')
    } catch (e: any) {
      if (e.name !== 'AbortError') { setError(e.message ?? 'Failed'); setPhase('error') }
    }
  }

  const stop = () => { abortRef.current?.abort(); setPhase('done') }
  const reset = () => { setPhase('idle'); setReport(''); setProgress([]); setSources([]); setQuery('') }

  const phaseIcon = (p: Phase) => {
    switch (p) {
      case 'planning': return '🧠'
      case 'searching': return '🔍'
      case 'reading': return '📖'
      case 'synthesizing': return '✍️'
      case 'done': return '✅'
      case 'error': return '❌'
      default: return '💡'
    }
  }

  return (
    <div style={{ padding: 16, height: '100%', overflow: 'auto' }}>
      <div style={{ marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: 16, color: 'var(--text-0)' }}>🔬 Deep Research Agent</h2>
        <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--text-2)' }}>
          Multi-step web search → read sources → synthesized report with citations
        </p>
      </div>

      {/* Input */}
      {phase === 'idle' && (
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="What do you want to research? e.g. 'Best practices for WebSocket scaling in Node.js'"
            onKeyDown={e => { if (e.key === 'Enter') startResearch() }}
            style={{
              flex: 1, padding: '10px 12px', borderRadius: 8,
              background: 'var(--bg-2)', border: '1px solid var(--border)',
              color: 'var(--text-0)', fontSize: 13, fontFamily: 'var(--font-mono)',
            }}
          />
          <button
            onClick={startResearch}
            disabled={!query.trim()}
            style={{
              padding: '8px 16px', borderRadius: 8, border: 'none',
              background: query.trim() ? 'var(--accent)' : 'var(--bg-3)',
              color: query.trim() ? '#fff' : 'var(--text-2)',
              cursor: query.trim() ? 'pointer' : 'not-allowed',
              fontWeight: 600, fontSize: 13,
            }}
          >
            Research
          </button>
        </div>
      )}

      {/* Progress */}
      {phase !== 'idle' && phase !== 'done' && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 13, color: 'var(--accent)', fontWeight: 600 }}>
              {phaseIcon(phase)} {phase.charAt(0).toUpperCase() + phase.slice(1)}...
            </span>
            <button onClick={stop} style={{
              padding: '4px 10px', borderRadius: 6, border: '1px solid var(--border)',
              background: 'var(--bg-2)', color: 'var(--text-1)', cursor: 'pointer', fontSize: 11,
            }}>
              Stop
            </button>
          </div>
          <div style={{ maxHeight: 120, overflow: 'auto', fontSize: 11, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>
            {progress.map((p, i) => (
              <div key={i} style={{ padding: '2px 0' }}>→ {p.message}</div>
            ))}
          </div>
        </div>
      )}

      {/* Report */}
      {report && (
        <div style={{
          background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 10,
          padding: 16, marginTop: 8, fontSize: 13, color: 'var(--text-1)',
          lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          maxHeight: 'calc(100vh - 280px)', overflow: 'auto',
        }}>
          <ReportRenderer text={report} />
        </div>
      )}

      {/* Sources */}
      {sources.length > 0 && phase === 'done' && (
        <div style={{ marginTop: 12, padding: 10, background: 'var(--bg-1)', borderRadius: 8, border: '1px solid var(--border)' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-0)', marginBottom: 6 }}>
            📚 Sources ({sources.length})
          </div>
          {sources.map((s, i) => (
            <div key={i} style={{ fontSize: 11, padding: '3px 0' }}>
              <a href={s.url} target="_blank" rel="noopener" style={{ color: 'var(--accent)', textDecoration: 'none' }}>
                [{i + 1}] {s.title}
              </a>
            </div>
          ))}
        </div>
      )}

      {/* Done actions */}
      {phase === 'done' && report && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button onClick={reset} style={{
            padding: '8px 16px', borderRadius: 8, border: 'none',
            background: 'var(--accent)', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 12,
          }}>
            New Research
          </button>
          <button onClick={() => navigator.clipboard.writeText(report)} style={{
            padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)',
            background: 'var(--bg-2)', color: 'var(--text-1)', cursor: 'pointer', fontSize: 12,
          }}>
            Copy Report
          </button>
        </div>
      )}

      {error && <div style={{ color: 'var(--red)', marginTop: 8, fontSize: 12 }}>❌ {error}</div>}
    </div>
  )
}

function ReportRenderer({ text }: { text: string }) {
  // Simple markdown-like rendering
  const html = text
    .replace(/^## (.+)$/gm, '<div style="font-weight:700;color:var(--accent);margin:12px 0 6px;font-size:14px">$1</div>')
    .replace(/^### (.+)$/gm, '<div style="font-weight:600;color:var(--text-0);margin:8px 0 4px;font-size:13px">$1</div>')
    .replace(/\*\*(.+?)\*\*/g, '<strong style="color:var(--text-0)">$1</strong>')
    .replace(/`([^`]+)`/g, '<code style="background:var(--bg-3);padding:1px 4px;border-radius:3px;font-family:var(--font-mono);font-size:11px">$1</code>')
    .replace(/\[(\d+)\]/g, '<sup style="color:var(--accent);font-size:10px">[$1]</sup>')
    .replace(/^- (.+)$/gm, '<div style="padding-left:12px">• $1</div>')
    .replace(/```(\w*)\n([\s\S]*?)```/g, '<pre style="background:var(--bg-3);padding:10px;border-radius:6px;font-family:var(--font-mono);font-size:11px;overflow-x:auto;margin:8px 0">$2</pre>')
  return <div dangerouslySetInnerHTML={{ __html: html }} />
}
